import React from 'react';
import BForm from '../../../Tools/Form/BForm';
import {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { ExocetFamilyType, ExocetRadioModeType } from '../Types';
import { DefaultRadioConfig, RadioConfig } from './types';
import { Autocomplete, Box, Chip, Divider, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { array, number, object, string } from 'yup';
import { FormikValues, useFormikContext } from 'formik';
import { MuiLikeBorderedBox } from '../../../Tools/Form/FormFieldWrapper';
import TrueFalse from '../../../Tools/TrueFalse';
import { Client, OnChangeArgs } from '../../../DndComponents/ContractsDnd/types';
import axios from 'axios';
import ClientsAndContracts from '../../../DndComponents/ContractsDnd/ClientsAndContracts';
import { isEqual } from 'lodash';
import { getAuthorisedData, getContracts, getCustomers, translate } from './utils';
import { useGlobalContext } from '@applications-terrains/birdz-react-library';

const DefaultConfigurationForm = ({
  setRequestStatus,
  action,
  setAction,
  config,
  moduleFamilies,
  radioModes,
  setErrorMessage,
  onClose,
  triggerRefresh,
  customers,
  configurations,
  defaultConfigurations
}: {
  setRequestStatus: (value: RequestStatus) => void;
  action: ModalWithLoaderAction | 'createDefault' | 'askingForDefault';
  setAction: (value: ModalWithLoaderAction) => void;
  config: DefaultRadioConfig | null;
  moduleFamilies?: ExocetFamilyType[];
  radioModes?: ExocetRadioModeType[];
  setErrorMessage: (value: string | null) => void;
  onClose: () => void;
  triggerRefresh: () => void;
  customers: Client[];
  configurations: RadioConfig[];
  defaultConfigurations: DefaultRadioConfig[];
}) => {
  const { t } = useTranslation();
  const { bgColor } = useGlobalContext();
  const isDark = bgColor === 'dark-mode';

  const configuration = config
    ? configurations.find((conf) => conf.id === config.profile) || null
    : null;

  const defaultConfig = {
    configuration: config
      ? {
          ...config,
          // familles du profil parent : config.families ne porte que le sous-ensemble
          // retenu par la configuration par défaut, pas les familles proposables
          families: configuration?.families ?? config.families,
          hourly_index: config.profile_hourly_index,
          radio_mode: config.profile_radio_mode,
          authorisedCustomers: getCustomers(config.customers || [], customers),
          authorisedContracts: getContracts(config.contracts || [], customers)
        }
      : null,
    families: config?.families
      ? config.families
          .map((familyId) => {
            const family = moduleFamilies?.find((fam) => fam.Id === familyId);
            return family
              ? {
                  label: translate(family.Name),
                  value: family.Id
                }
              : null;
          })
          .filter(Boolean)
      : [],
    clientsAndContracts:
      action === 'edit' || action === 'createDefault'
        ? getAuthorisedData(
            customers,
            configuration?.authorised_customers || [],
            configuration?.authorised_contracts || [],
            configuration?.excluded_customers || [],
            configuration?.excluded_contracts || []
          )
        : undefined
  };

  const formValues = [
    {
      name: 'configuration',
      label: '',
      render: () => {
        const { setFieldValue, setValues, validateField, values }: FormikValues =
          useFormikContext();
        const configuration = values?.configuration;
        const options = configurations?.filter(
          (config) => !defaultConfigurations.some((defConfig) => defConfig.profile === config.id)
        );
        const selectedOption = options?.find((option) => option.id === configuration?.id);
        const value = selectedOption
          ? { label: selectedOption.name, value: selectedOption.id }
          : null;
        return (
          (action === 'add' || action === 'createDefault') && (
            <Autocomplete
              size="small"
              value={value}
              options={
                options.map((configuration) => ({
                  label: configuration.name,
                  value: configuration.id
                })) || []
              }
              onChange={async (_, newValue) => {
                if (!newValue) {
                  setValues({
                    configuration: null,
                    families: [],
                    authorised_customers: [],
                    authorised_contracts: []
                  });
                  return;
                }
                const config = configurations.find((config) => {
                  return config.id === newValue?.value;
                });

                await setFieldValue(
                  'configuration',
                  {
                    ...config,
                    authorisedCustomers: config?.authorised_customers,
                    authorisedContracts: config?.authorised_contracts
                  },
                  false
                );
                if (config) {
                  setFieldValue(
                    'families',
                    config.families
                      .map((familyId) => {
                        const family = moduleFamilies?.find((fam) => fam.Id === familyId);
                        return family ? { label: translate(family.Name), value: family.Id } : null;
                      })
                      .filter(Boolean) || [],
                    false
                  );
                  setFieldValue(
                    'clientsAndContracts',
                    getAuthorisedData(
                      customers,
                      config?.authorised_customers || [],
                      config?.authorised_contracts || [],
                      config?.excluded_customers || [],
                      config?.excluded_contracts || []
                    ),
                    false
                  );
                }
                setTimeout(() => {
                  validateField('configuration');
                }, 0);
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('equipmentConfiguration.configuration')} />
              )}
              isOptionEqualToValue={(option, value) => {
                return option.value === value.value;
              }}
              getOptionLabel={(option) => {
                return option.label || '';
              }}
              fullWidth
            />
          )
        );
      },
      validation: object()
        .shape({ label: string(), value: number() })
        .required(t('equipmentConfiguration.requiredConfiguration'))
    },
    {
      name: 'families',
      label: '',
      render: (value: { label: string; value: number }[]) => {
        const { setFieldValue, values }: FormikValues = useFormikContext();
        const configuration = values?.configuration;
        const parentFamilyIds = (configuration?.families as number[]) || [];
        const familyOptions = (moduleFamilies || [])
          .filter((family) => parentFamilyIds.includes(family.Id))
          .map((family) => ({ label: translate(family.Name), value: family.Id }));

        return (
          <Autocomplete
            sx={{ visibility: configuration ? 'visible' : 'hidden' }}
            size="small"
            multiple
            value={value || []}
            options={familyOptions}
            onChange={(_, newValue) => {
              setFieldValue('families', newValue || []);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.value === value.value;
            }}
            getOptionLabel={(option) => {
              return option.label || '';
            }}
            renderInput={(params) => (
              <TextField {...params} label={t('equipmentConfiguration.families')} />
            )}
            fullWidth
          />
        );
      },
      validation: array().min(1, t('interventionTypesForm.errors.familieMin'))
    },
    {
      name: 'info',
      label: '',
      render: () => {
        const { values }: FormikValues = useFormikContext();
        const configuration = values?.configuration;
        const radioModeLabel = radioModes?.find(
          (radioMode) => radioMode.Id === configuration?.radio_mode
        )?.Label;
        const hourlyIndex = configuration?.hourly_index ?? null;
        return (
          <MuiLikeBorderedBox
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              visibility: configuration ? 'visible' : 'hidden'
            }}
          >
            <Box display={'flex'} gap={1} alignItems={'center'}>
              <Typography sx={{ fontSize: 'body2.fontSize', color: 'rgba(0, 0, 0, 0.6)' }}>
                {t('equipmentConfiguration.hourly_index')}
              </Typography>
              <TrueFalse value={hourlyIndex === 'True' || hourlyIndex === true} />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
            <Box display={'flex'} gap={1} alignItems={'center'}>
              <Typography sx={{ fontSize: 'body2.fontSize', color: 'rgba(0, 0, 0, 0.6)' }}>
                {t('equipmentConfiguration.radioMode')}
              </Typography>
              <Chip size="small" label={radioModeLabel} />
            </Box>
          </MuiLikeBorderedBox>
        );
      }
    },
    {
      name: 'clientsAndContracts',
      label: '',
      render: () => {
        const { values, setFieldValue }: FormikValues = useFormikContext();
        const handleChange = ({ authorisedContracts, authorisedCustomers }: OnChangeArgs) => {
          if (!values?.configuration) return;

          if (!isEqual(values.configuration?.authorisedCustomers, authorisedCustomers)) {
            setFieldValue('configuration.authorisedCustomers', authorisedCustomers);
          }

          if (!isEqual(values.configuration?.authorisedContracts, authorisedContracts)) {
            setFieldValue('configuration.authorisedContracts', authorisedContracts);
          }
        };

        return values?.configuration && values?.clientsAndContracts?.length > 0 ? (
          <ClientsAndContracts
            key={values?.clientsAndContracts?.length}
            data={values?.clientsAndContracts?.length > 0 ? values.clientsAndContracts : undefined}
            target="default"
            onChange={handleChange}
            initialAuthorisedContracts={(values?.configuration?.authorisedContracts || []).map(
              (c: { value: number } | number) => (typeof c === 'object' ? c.value : c)
            )}
            initialExcludedContracts={configuration?.excluded_contracts}
            initialAuthorisedCustomers={(values?.configuration?.authorisedCustomers || []).map(
              (c: { value: number } | number) => (typeof c === 'object' ? c.value : c)
            )}
            initialExcludedCustomers={configuration?.excluded_customers}
          />
        ) : null;
      }
    }
  ];

  const handleSubmit = async (values: FormikValues) => {
    setRequestStatus('pending');
    const endpoint = '/api/boi/params/default-configurations/';
    const method = action === 'add' || action === 'createDefault' ? 'post' : 'put';
    const url = `${endpoint}${method === 'post' ? '' : +(config as DefaultRadioConfig & { id: number })?.id + '/'}`;

    const clientsToSubmit =
      (values.configuration?.authorisedCustomers || []).length > 0
        ? (values.configuration?.authorisedCustomers || []).map((c: { value: number } | number) =>
            typeof c === 'object' ? c.value : c
          )
        : (values.configuration?.customers || []).map(
            (customer: { label: string; value: number }) => {
              const selectedContracts =
                values?.configuration?.contracts?.filter(
                  (contract: { clientName: string }) => contract.clientName === customer.label
                ) || [];
              return selectedContracts.length > 0 ? null : customer.value;
            }
          );
    const contractsToSubmit =
      (values.configuration?.authorisedContracts || []).length > 0
        ? (values.configuration?.authorisedContracts || []).map((c: { value: number } | number) =>
            typeof c === 'object' ? c.value : c
          )
        : (values.configuration?.contracts || []).map(
            (contract: { label: string; value: number }) => contract.value
          );
    const formattedValues = {
      profile: action === 'add' ? values.configuration.id : (config as DefaultRadioConfig).profile,
      families: (values.families || []).map(
        (family: { label: string; value: number }) => family.value
      ),
      customers: clientsToSubmit.filter(Boolean),
      contracts: contractsToSubmit.filter(Boolean)
    };
    return axios[method](url, formattedValues)
      .then(() => {
        setRequestStatus('success');
        triggerRefresh();
        setTimeout(() => {
          onClose();
          setAction(null);
        }, 1500);
      })
      .catch((err) => {
        const error =
          typeof err.response?.data === 'object'
            ? Object.values(err.response.data).flat().join(' ')
            : err.response?.data || 'An error occurred';
        setRequestStatus('error');
        setErrorMessage(error);
      });
  };

  return (
    <Box mt={action === 'edit' ? -3 : 0}>
      <BForm
        inModal
        columns={1}
        values={defaultConfig || {}}
        fields={formValues}
        onSubmit={handleSubmit}
        onCancel={() => {
          setAction(null);
          onClose();
        }}
        buttonsContainerSx={{
          position: 'absolute',
          bottom: 0,
          right: 2,
          backgroundColor: isDark ? '#1E2235' : '#FFF',
          borderTop: '1px solid',
          borderColor: 'divider',
          padding: 2
        }}
        sx={{ pb: 10, pt: action === 'edit' ? 0 : 1 }}
      />
    </Box>
  );
};

export default DefaultConfigurationForm;
